/**
 * RTWO Cryptography & Identity Engine — Key Pair Generation Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { KeyPairId, KeyStatus, RTWOKeyPair } from '../domain-models';
import type {
  IKeyPairGenerationService,
  KeyPairGenerationRequest,
  KeyPairGenerationResult,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { KeyProviderRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class KeyPairGenerationService
  extends BaseCryptoService
  implements IKeyPairGenerationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.KEY_PAIR_GENERATION;
  readonly serviceName = 'Key Pair Generation Service';
  readonly description =
    'Requests key pair generation from the Key Provider. Private keys are never returned.';
  readonly layer = 'R6' as const;
  readonly integrations = ['R6 Security & Automation', 'Key Provider'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = false;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly keyProviderRegistry: KeyProviderRegistry
  ) {
    super(context);
  }

  async requestKeyPairGeneration(
    request: KeyPairGenerationRequest
  ): Promise<KeyPairGenerationResult> {
    this.context.eventBus.emit(CryptoEventTypes.KEY_PAIR_REQUESTED, request.requestedBy, {
      algorithm: request.algorithm,
      purpose: request.purpose,
    });

    const provider = this.keyProviderRegistry.getActiveProvider();
    if (!this.keyProviderRegistry.isConfigured() || !provider.isReady()) {
      return {
        success: false,
        keyPairId: null,
        publicKey: null,
        privateKeyReference: null,
        error: 'Key Provider not configured or not connected. No keys generated.',
        isPlaceholder: true,
        source: 'Key Provider — Not Configured',
      };
    }

    const result = await provider.generateKeyPair(request);
    if (!result?.keyPairId || !result.publicKey || !result.privateKeyReference) {
      return {
        success: false,
        keyPairId: null,
        publicKey: null,
        privateKeyReference: null,
        error: 'Key Provider did not return a key pair.',
        isPlaceholder: true,
        source: 'Key Provider — Operation Unavailable',
      };
    }

    const keyPair: RTWOKeyPair = {
      keyPairId: result.keyPairId,
      publicKey: result.publicKey,
      privateKeyReference: result.privateKeyReference,
      algorithm: request.algorithm,
      curve: request.curve,
      status: 'generated',
      createdAt: Date.now(),
      rotatedAt: null,
    };

    this.repositories.keyPairs.save(keyPair);
    this.context.eventBus.emit(CryptoEventTypes.KEY_PAIR_GENERATED, result.keyPairId, {
      algorithm: request.algorithm,
    });

    return {
      success: true,
      keyPairId: result.keyPairId,
      publicKey: result.publicKey,
      privateKeyReference: result.privateKeyReference,
      error: null,
      source: 'Key Provider — Key Pair Generated',
    };
  }

  getKeyPair(keyPairId: KeyPairId): RTWOKeyPair | null {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return null;
    }

    return this.repositories.keyPairs.findById(keyPairId);
  }

  getKeyPairsByStatus(status: KeyStatus): RTWOKeyPair[] {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return [];
    }

    return this.repositories.keyPairs.findByStatus(status);
  }

  getKeyPairCount(): number | null {
    if (!this.keyProviderRegistry.getActiveProvider().isReady()) {
      return null;
    }

    return this.repositories.keyPairs.count();
  }
}
